(() => {
  const RUNTIME_KEY = 'PawlishProductCartCoordinatorRuntime';
  const LOADER_KEY = '__pawlishProductCartCoordinatorLoader';
  const VERSION = '2E';
  const previousRuntime = window[RUNTIME_KEY];

  if (previousRuntime && previousRuntime.abortController) {
    previousRuntime.abortController.abort();
  }

  const abortController = new AbortController();
  const listenerOptions = { capture: true, signal: abortController.signal };

  window[RUNTIME_KEY] = { version: VERSION, abortController };

  const pendingRequests = new WeakMap();
  const controlStates = new WeakMap();
  const mainButtonStates = new WeakMap();
  const INTERNAL_ROLE_PROPERTY = '_pawlish_cart_role';
  const GIFT_ROLE_PREFIX = 'gift:';

  function getQuantity(value) {
    const quantity = parseInt(value || '1', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function getQuantityRule(input, attribute, fallback) {
    if (!input) return fallback;

    const rawValue = input.getAttribute(attribute);
    if (rawValue === null || String(rawValue).trim() === '' || rawValue === 'any') return fallback;

    const parsedValue = Number(rawValue);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) return fallback;
    return Math.max(1, Math.round(parsedValue));
  }

  function getQuantityRules(input) {
    const min = getQuantityRule(input, 'min', 1);
    const step = getQuantityRule(input, 'step', 1);
    let max = getQuantityRule(input, 'max', null);

    if (max !== null && max < min) max = min;

    return { min, max, step };
  }

  function getAlignedMaximum(rules) {
    if (!rules || rules.max === null) return null;
    return rules.min + Math.floor((rules.max - rules.min) / rules.step) * rules.step;
  }

  function normalizeQuantityInput(input) {
    const rules = getQuantityRules(input);
    const rawValue = input && String(input.value || '').trim() !== '' ? Number(input.value) : rules.min;
    const numericValue = Number.isFinite(rawValue) ? rawValue : rules.min;
    const alignedMaximum = getAlignedMaximum(rules);
    let quantity = rules.min + Math.round((numericValue - rules.min) / rules.step) * rules.step;

    quantity = Math.max(rules.min, quantity);
    if (alignedMaximum !== null) quantity = Math.min(alignedMaximum, quantity);
    quantity = Math.max(1, Math.round(quantity));

    if (input && String(input.value) !== String(quantity)) {
      input.value = String(quantity);
    }

    return quantity;
  }

  function cloneProperties(properties) {
    if (!Array.isArray(properties)) return [];
    return properties.map((property) => ({ key: property.key, value: property.value }));
  }

  function getFormMetadata(form) {
    const metadata = { properties: [], sellingPlan: null };
    if (!form) return metadata;

    const sourceFormData = new FormData(form);

    sourceFormData.forEach((value, name) => {
      const propertyMatch = /^properties\[(.*)\]$/.exec(name);
      if (!propertyMatch) return;

      metadata.properties.push({
        key: propertyMatch[1],
        value,
      });
    });

    const sellingPlan = sourceFormData.get('selling_plan');
    if (sellingPlan !== null && String(sellingPlan).trim() !== '') {
      metadata.sellingPlan = String(sellingPlan);
    }

    return metadata;
  }

  function withMainMetadata(item, metadata) {
    return Object.assign({}, item, {
      role: 'main',
      properties: cloneProperties(metadata && metadata.properties),
      sellingPlan: metadata && metadata.sellingPlan ? metadata.sellingPlan : null,
    });
  }

  function getFormDataValueSignature(value) {
    if (typeof File !== 'undefined' && value instanceof File) {
      return `file:${value.name}:${value.size}:${value.type}:${value.lastModified}`;
    }

    return `text:${String(value)}`;
  }

  function getPropertiesSignature(properties) {
    return cloneProperties(properties)
      .map((property) => `${property.key}=${getFormDataValueSignature(property.value)}`)
      .sort()
      .join('\u001f');
  }

  function getItemSignature(item) {
    return [
      item.id,
      item.role || 'main',
      item.sellingPlan || '',
      getPropertiesSignature(item.properties),
    ].join('\u001e');
  }

  function getRequestProperties(item) {
    const properties = cloneProperties(item.properties);
    if (!item.role || item.role === 'main') return properties;

    return properties
      .filter((property) => property.key !== INTERNAL_ROLE_PROPERTY)
      .concat({ key: INTERNAL_ROLE_PROPERTY, value: item.role });
  }

  function getProductRoot(element, sectionId) {
    if (element && typeof element.closest === 'function') {
      const closestRoot = element.closest('product-info[data-section]');
      if (closestRoot) return closestRoot;
    }

    if (!sectionId) return null;

    return (
      document.getElementById(`MainProduct-${sectionId}`) ||
      Array.from(document.querySelectorAll('product-info[data-section]')).find(
        (root) => root.dataset.section === String(sectionId)
      ) ||
      null
    );
  }

  function getProductForm(root) {
    if (!root) return null;
    return root.querySelector('form[data-pro-cart-coordinator-form]');
  }

  function getProductFormElement(form) {
    return form ? form.closest('product-form') : null;
  }

  function getMainButton(form) {
    const productForm = getProductFormElement(form);
    return productForm ? productForm.querySelector('[type="submit"]') : null;
  }

  function getSelectedBundleCard(root) {
    if (!root) return null;
    const checked =
      root.querySelector('[data-pro-bundle-root] .bqu-radio:checked') || root.querySelector('.bqu-radio:checked');
    return checked ? checked.closest('.bqu-card') : null;
  }

  function getSelectedUpsellItems(root) {
    const items = [];
    if (!root) return items;

    root.querySelectorAll('[data-pro-upsell-card]').forEach((card) => {
      const checkbox = card.querySelector('[data-pro-upsell-checkbox]');
      if (!checkbox || !checkbox.checked || checkbox.disabled) return;

      const variantId = parseInt(card.dataset.variantId || '0', 10);
      const quantity = getQuantity(card.dataset.quantity || '1');

      if (variantId) {
        items.push({ id: variantId, quantity, role: 'upsell', properties: [], sellingPlan: null });
      }
    });

    return items;
  }

  function getSelectedOption(select) {
    if (!select || !select.selectedOptions || !select.selectedOptions.length) return null;

    const option = select.selectedOptions[0];
    return {
      id: parseInt(option.value || '0', 10),
      unavailable: option.disabled,
    };
  }

  function getBundleItems(card, form, metadata) {
    const items = [];
    const grouped = {};
    const selects = card ? Array.from(card.querySelectorAll('.bqu-unit-variant-select')) : [];

    if (selects.length) {
      for (const select of selects) {
        const selectedOption = getSelectedOption(select);

        if (!selectedOption || !selectedOption.id) {
          throw new Error('Please choose an option for every item in your bundle.');
        }

        if (selectedOption.unavailable) {
          throw new Error('One of the selected bundle options is sold out. Please choose another option.');
        }

        grouped[selectedOption.id] = (grouped[selectedOption.id] || 0) + 1;
      }
    } else if (card && form) {
      const radio = card.querySelector('.bqu-radio');
      const variantInput = form.elements.namedItem('id');
      const mainButton = getMainButton(form);
      const variantId = variantInput ? parseInt(variantInput.value || '0', 10) : 0;
      const quantity = radio ? getQuantity(radio.value) : 1;
      const isUnavailable = !variantId || (mainButton && mainButton.disabled);

      if (isUnavailable) {
        throw new Error('The selected product option is unavailable.');
      }

      grouped[variantId] = quantity;
    }

    Object.keys(grouped).forEach((id) => {
      items.push(
        withMainMetadata(
          { id: parseInt(id, 10), quantity: grouped[id] },
          metadata
        )
      );
    });

    return items;
  }


  function getNativeItem(form, metadata) {
    if (!form) return null;

    const variantInput = form.elements.namedItem('id');
    const quantityInput = form.elements.namedItem('quantity');
    const variantId = variantInput ? parseInt(variantInput.value || '0', 10) : 0;

    if (!variantId) return null;

    return withMainMetadata(
      {
        id: variantId,
        quantity: quantityInput ? normalizeQuantityInput(quantityInput) : 1,
      },
      metadata
    );
  }

  function groupItems(items) {
    const grouped = new Map();

    items.forEach((item) => {
      if (!item) return;

      const id = parseInt(item.id || '0', 10);
      const quantity = parseInt(item.quantity || '0', 10);
      if (!id || !quantity) return;

      const normalizedItem = {
        id,
        quantity,
        role: item.role || 'main',
        properties: cloneProperties(item.properties),
        sellingPlan: item.sellingPlan ? String(item.sellingPlan) : null,
      };
      const signature = getItemSignature(normalizedItem);
      const existingItem = grouped.get(signature);

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        grouped.set(signature, normalizedItem);
      }
    });

    return Array.from(grouped.values());
  }

  function shouldEnhance(form, root) {
    if (!form || !root) return false;
    return (
      Boolean(getSelectedBundleCard(root)) ||
      getSelectedUpsellItems(root).length > 0 ||
      hasEligibleFreeGift(root, form)
    );
  }

  function isThemeEditor() {
    return Boolean(window.Shopify && Shopify.designMode);
  }

  function getThemeEditorNativeBundleItem(form, root) {
    if (!isThemeEditor() || !form || !root) return null;

    const bundleCard = getSelectedBundleCard(root);
    if (!bundleCard) return null;
    if (getSelectedUpsellItems(root).length > 0 || hasEligibleFreeGift(root, form)) return null;

    try {
      const metadata = getFormMetadata(form);
      const items = groupItems(getBundleItems(bundleCard, form, metadata));

      if (items.length !== 1 || items[0].role !== 'main') return null;
      return items[0];
    } catch (error) {
      return null;
    }
  }

  function prepareThemeEditorNativeBundle(form, root) {
    const item = getThemeEditorNativeBundleItem(form, root);
    if (!item) return null;

    const variantInput = form.elements.namedItem('id');
    if (!variantInput || typeof variantInput.value === 'undefined') return null;

    const previousVariantValue = variantInput.value;
    const previousVariantDisabled = variantInput.disabled;
    const existingQuantityInput = form.querySelector('[name="quantity"]');
    const previousQuantityValue = existingQuantityInput ? existingQuantityInput.value : null;
    const quantityInput = existingQuantityInput || document.createElement('input');

    if (!existingQuantityInput) {
      quantityInput.type = 'hidden';
      quantityInput.name = 'quantity';
      quantityInput.dataset.proThemeEditorBundleQuantity = 'true';
      form.appendChild(quantityInput);
    }

    variantInput.disabled = false;
    variantInput.value = String(item.id);
    quantityInput.value = String(item.quantity);

    let cleaned = false;
    return {
      item,
      cleanup() {
        if (cleaned) return;
        cleaned = true;
        variantInput.value = previousVariantValue;
        variantInput.disabled = previousVariantDisabled;

        if (existingQuantityInput) {
          existingQuantityInput.value = previousQuantityValue;
        } else if (quantityInput.isConnected) {
          quantityInput.remove();
        }
      },
    };
  }

  function schedulePreparedCleanup(prepared) {
    if (!prepared) return;
    window.setTimeout(() => prepared.cleanup(), 0);
  }

  async function buildItems(form, root) {
    const bundleCard = getSelectedBundleCard(root);
    const metadata = getFormMetadata(form);
    const mainQuantity = getMainPurchaseQuantity(root, form, bundleCard);
    const items = [];

    if (bundleCard) {
      items.push(...getBundleItems(bundleCard, form, metadata));
    } else {
      const nativeItem = getNativeItem(form, metadata);
      if (nativeItem) items.push(nativeItem);
    }

    items.push(...getSelectedUpsellItems(root));
    items.push(...(await getEligibleFreeGiftItems(root, mainQuantity)));
    return groupItems(items);
  }

  function getMainPurchaseQuantity(root, form, bundleCard) {
    if (bundleCard) {
      const radio = bundleCard.querySelector('.bqu-radio');
      if (radio) return getQuantity(radio.value);
    }

    if (!form) return 1;
    const quantityInput = form.elements.namedItem('quantity');
    return quantityInput ? normalizeQuantityInput(quantityInput) : 1;
  }

  function getFreeGiftBlocks(root) {
    if (!root) return [];
    return Array.from(root.querySelectorAll('[data-pro-free-gift]'));
  }

  function isFreeGiftEligible(block, mainQuantity) {
    if (!block || block.dataset.giftAvailable !== 'true') return false;
    if (!parseInt(block.dataset.variantId || '0', 10)) return false;
    if (block.dataset.eligibility === 'always') return true;

    const minimumQuantity = getQuantity(block.dataset.minimumQuantity || '1');
    return mainQuantity >= minimumQuantity;
  }

  function hasEligibleFreeGift(root, form) {
    const bundleCard = getSelectedBundleCard(root);
    const mainQuantity = getMainPurchaseQuantity(root, form, bundleCard);
    return getFreeGiftBlocks(root).some((block) => isFreeGiftEligible(block, mainQuantity));
  }

  function getCartJsonUrl() {
    const root = window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
    return `${root}cart.js`;
  }

  async function fetchCartState() {
    try {
      const response = await fetch(getCartJsonUrl(), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        cache: 'no-store',
      });

      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}.`);
      return await response.json();
    } catch (error) {
      console.warn('[PawlishProductCartCoordinator] Unable to verify the current gift quantity.', error);
      return null;
    }
  }

  function getCartLineRole(item) {
    if (!item || !item.properties) return '';
    return String(item.properties[INTERNAL_ROLE_PROPERTY] || '');
  }

  function cartHasGiftRole(cartState, giftRole) {
    if (!cartState || !Array.isArray(cartState.items)) return false;
    return cartState.items.some((item) => getCartLineRole(item) === giftRole);
  }

  async function getEligibleFreeGiftItems(root, mainQuantity) {
    const eligibleBlocks = getFreeGiftBlocks(root).filter((block) =>
      isFreeGiftEligible(block, mainQuantity)
    );

    if (!eligibleBlocks.length) return [];

    const needsCartCheck = eligibleBlocks.some(
      (block) => block.dataset.giftLimit === 'one_per_cart'
    );
    const cartState = needsCartCheck ? await fetchCartState() : null;
    const items = [];

    eligibleBlocks.forEach((block) => {
      const variantId = parseInt(block.dataset.variantId || '0', 10);
      const blockId = String(block.dataset.blockId || '').trim();
      if (!variantId || !blockId) return;

      const giftRole = `${GIFT_ROLE_PREFIX}${blockId}`;
      const baseGiftQuantity = getQuantity(block.dataset.giftQuantity || '1');
      const limit = block.dataset.giftLimit || 'one_per_cart';
      let quantity = baseGiftQuantity;

      if (limit === 'one_per_cart') {
        const fallbackCartQuantity = parseInt(block.dataset.currentCartQuantity || '0', 10) || 0;
        const alreadyInCart = cartState
          ? cartHasGiftRole(cartState, giftRole)
          : fallbackCartQuantity > 0 || block.dataset.giftAdded === 'true';

        if (alreadyInCart) return;
      } else {
        quantity = baseGiftQuantity * Math.max(1, mainQuantity);
      }

      items.push({
        id: variantId,
        quantity,
        role: giftRole,
        properties: [],
        sellingPlan: null,
      });
    });

    return items;
  }

  function markGiftItemsAdded(root, items) {
    if (!root || !Array.isArray(items)) return;

    items.forEach((item) => {
      if (!item.role || !item.role.startsWith(GIFT_ROLE_PREFIX)) return;

      const blockId = item.role.slice(GIFT_ROLE_PREFIX.length);
      const block = root.querySelector(
        `[data-pro-free-gift][data-block-id="${window.CSS && CSS.escape ? CSS.escape(blockId) : blockId}"]`
      );
      if (!block) return;

      const currentQuantity = parseInt(block.dataset.currentCartQuantity || '0', 10) || 0;
      block.dataset.currentCartQuantity = String(currentQuantity + item.quantity);
      block.dataset.giftAdded = 'true';

      const select = block.querySelector('[data-pro-free-gift-variant]');
      const option = select && select.selectedOptions ? select.selectedOptions[0] : null;
      if (option) option.dataset.cartQuantity = block.dataset.currentCartQuantity;
    });
  }

  function rememberControl(control) {
    if (!control || controlStates.has(control)) return;

    controlStates.set(control, {
      disabled: control.disabled,
      ariaDisabled: control.getAttribute('aria-disabled'),
      ariaBusy: control.getAttribute('aria-busy'),
      text: control.textContent,
      hadLoadingClass: control.classList.contains('loading'),
      hadStickyLoadingClass: control.classList.contains('is-loading'),
    });
  }

  function restoreControl(control) {
    const state = controlStates.get(control);
    if (!control || !state) return;

    control.disabled = state.disabled;

    if (state.ariaDisabled === null) {
      control.removeAttribute('aria-disabled');
    } else {
      control.setAttribute('aria-disabled', state.ariaDisabled);
    }

    if (state.ariaBusy === null) {
      control.removeAttribute('aria-busy');
    } else {
      control.setAttribute('aria-busy', state.ariaBusy);
    }

    delete control.dataset.proCartLoading;
    control.classList.toggle('loading', state.hadLoadingClass);
    control.classList.toggle('is-loading', state.hadStickyLoadingClass);
    control.textContent = state.text;
    controlStates.delete(control);
  }

  function setMainButtonLoading(productForm, isLoading) {
    if (!productForm) return;

    const button = productForm.querySelector('[type="submit"]');
    const spinner = productForm.querySelector('.loading__spinner');
    if (!button) return;

    if (isLoading) {
      if (!mainButtonStates.has(button)) {
        mainButtonStates.set(button, {
          ariaDisabled: button.getAttribute('aria-disabled'),
          hadLoadingClass: button.classList.contains('loading'),
          spinnerWasHidden: spinner ? spinner.classList.contains('hidden') : true,
        });
      }

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
      return;
    }

    const state = mainButtonStates.get(button);
    if (!state) return;

    if (state.ariaDisabled === null) {
      button.removeAttribute('aria-disabled');
    } else {
      button.setAttribute('aria-disabled', state.ariaDisabled);
    }

    button.classList.toggle('loading', state.hadLoadingClass);
    if (spinner) spinner.classList.toggle('hidden', state.spinnerWasHidden);
    mainButtonStates.delete(button);
  }

  function setSecondaryButtonLoading(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      rememberControl(button);
      button.disabled = true;
      button.dataset.proCartLoading = 'true';
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');

      if (button.matches('[data-pro-sticky-atc-submit]')) {
        button.classList.add('is-loading');
        button.textContent = button.dataset.loadingText || 'Adding...';
      } else {
        button.textContent = button.dataset.loadingText || 'Adding...';
      }
      return;
    }

    restoreControl(button);
  }

  function setEnhancedLoading(root, productForm, isLoading) {
    setMainButtonLoading(productForm, isLoading);

    if (!root) return;

    root
      .querySelectorAll(
        '[data-pro-bundle-proxy], .bqu-btn--proxy[id^="bqu-btn-"], [data-pro-sticky-atc-submit]'
      )
      .forEach((button) => {
        setSecondaryButtonLoading(button, isLoading);
      });
  }

  function refreshBundleControls(root) {
    if (!root) return;

    const bundleRuntime = window.PawlishBundleQuantityUpsell;
    if (!bundleRuntime || typeof bundleRuntime.updateRoot !== 'function') return;

    root.querySelectorAll('[data-pro-bundle-root]').forEach((bundleRoot) => {
      try {
        bundleRuntime.updateRoot(bundleRoot);
      } catch (error) {
        console.warn('[PawlishProductCartCoordinator] Unable to refresh bundle controls.', error);
      }
    });
  }

  function showError(productForm, message) {
    if (!productForm) return;

    const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
    const errorMessage = productForm.querySelector('.product-form__error-message');
    if (!wrapper || !errorMessage) return;

    errorMessage.textContent = message || '';
    wrapper.toggleAttribute('hidden', !message);
  }

  function getCartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function getCartAddUrl() {
    if (window.routes && window.routes.cart_add_url) return window.routes.cart_add_url;
    if (window.Shopify && Shopify.routes && Shopify.routes.root) return `${Shopify.routes.root}cart/add.js`;
    return '/cart/add.js';
  }

  function getCartUrl() {
    return window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
  }

  function getMainVariantId(form) {
    if (!form) return '';
    const variantInput = form.elements.namedItem('id');
    return variantInput ? String(variantInput.value || '') : '';
  }

  function appendItemToFormData(formData, item, index) {
    const prefix = `items[${index}]`;
    formData.append(`${prefix}[id]`, item.id);
    formData.append(`${prefix}[quantity]`, item.quantity);

    if (item.sellingPlan) {
      formData.append(`${prefix}[selling_plan]`, item.sellingPlan);
    }

    getRequestProperties(item).forEach((property) => {
      formData.append(`${prefix}[properties][${property.key}]`, property.value);
    });
  }

  function publishCartEvent(eventKey, payload) {
    if (
      typeof publish !== 'function' ||
      typeof PUB_SUB_EVENTS === 'undefined' ||
      !PUB_SUB_EVENTS[eventKey]
    ) {
      return Promise.resolve();
    }

    return Promise.resolve(publish(PUB_SUB_EVENTS[eventKey], payload)).catch((error) => {
      console.error(`[PawlishProductCartCoordinator] ${eventKey} subscriber failed`, error);
    });
  }

  function publishCartUpdate(response, productVariantId, source = 'product-form') {
    return publishCartEvent('cartUpdate', {
      source,
      productVariantId,
      cartData: response,
    });
  }

  function publishCartError(error, productVariantId, source = 'product-form') {
    return publishCartEvent('cartError', {
      source,
      productVariantId,
      errors: error && (error.errors || error.description || error.message),
      message: error && (error.message || error.description),
    });
  }

  function getStandardEventTarget(productForm, root) {
    return productForm || root || null;
  }

  function createCartLinesUpdateEvent(target, items) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent || !target || !Array.isArray(items) || !items.length) return null;

    const lines = items
      .map((item) => ({
        merchandiseId: String(item.id || ''),
        quantity: getQuantity(item.quantity),
      }))
      .filter((line) => line.merchandiseId);

    if (!lines.length) return null;

    try {
      const deferred = CartLinesUpdateEvent.createPromise();
      target.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines,
          promise: deferred.promise,
        })
      );
      return deferred;
    } catch (error) {
      console.warn('[PawlishProductCartCoordinator] Unable to dispatch CartLinesUpdateEvent.', error);
      return null;
    }
  }

  function fetchCartForStandardEvent() {
    if (
      typeof CartItems !== 'undefined' &&
      CartItems &&
      typeof CartItems.fetchCartData === 'function'
    ) {
      return CartItems.fetchCartData();
    }

    return fetch(getCartJsonUrl(), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      cache: 'no-store',
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Cart request failed with status ${response.status}.`);
      }
      return response.json();
    });
  }

  function resolveCartLinesUpdateEvent(deferred) {
    if (!deferred) return;

    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) {
      deferred.reject(new Error('Shopify Standard CartLinesUpdateEvent is unavailable.'));
      return;
    }

    fetchCartForStandardEvent()
      .then((cart) => {
        if (!cart || !cart.currency) {
          throw new Error('Missing currency in cart response.');
        }

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        });
      })
      .catch((error) => deferred.reject(error));
  }

  function rejectCartLinesUpdateEvent(deferred, error) {
    if (!deferred) return;
    deferred.reject(error instanceof Error ? error : new Error(String(error || 'Unable to update cart.')));
  }

  function dispatchCartErrorEvent(target, message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent || !target) return;

    try {
      target.dispatchEvent(
        new CartErrorEvent({
          error: message || 'Unable to add to cart.',
          code: code || 'SERVICE_UNAVAILABLE',
        })
      );
    } catch (error) {
      console.warn('[PawlishProductCartCoordinator] Unable to dispatch CartErrorEvent.', error);
    }
  }

  function dispatch(root, eventName, detail) {
    if (!root) return;
    root.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: Object.assign({ root, sectionId: root.dataset.section || '' }, detail || {}),
      })
    );
  }

  function renderCart(response, activeElement) {
    const cart = getCartElement();

    if (!cart) {
      window.location.assign(getCartUrl());
      return;
    }

    if (typeof cart.setActiveElement === 'function') {
      cart.setActiveElement(activeElement || document.activeElement);
    }

    if (cart.classList.contains('is-empty')) {
      cart.classList.remove('is-empty');
    }

    if (cart.tagName === 'CART-NOTIFICATION' && !response.key) {
      window.location.assign(getCartUrl());
      return;
    }

    if (response && response.sections && typeof cart.renderContents === 'function') {
      cart.renderContents(response);
      return;
    }

    if (typeof cart.open === 'function') {
      cart.open(activeElement || document.activeElement);
      return;
    }

    window.location.assign(getCartUrl());
  }

  async function submitPreparedItemsRequest(items, root, activeElement, options = {}) {
    const normalizedItems = groupItems(items);
    const productForm = options.productForm || null;
    const productVariantId =
      options.productVariantId || String(normalizedItems[0] ? normalizedItems[0].id : '');
    const source = options.source || 'product-form';
    const standardEventTarget =
      options.standardEventTarget || getStandardEventTarget(productForm, root);
    const useStandardEvents =
      typeof options.useStandardEvents === 'boolean' ? options.useStandardEvents : !isThemeEditor();
    let linesUpdateDeferred = null;
    let standardErrorCode = 'INVALID';
    let data = null;

    try {
      if (!normalizedItems.length) {
        throw new Error('No available products were selected.');
      }

      if (useStandardEvents) {
        linesUpdateDeferred = createCartLinesUpdateEvent(standardEventTarget, normalizedItems);
      }

      const cart = getCartElement();
      const formData = new FormData();

      normalizedItems.forEach((item, index) => {
        appendItemToFormData(formData, item, index);
      });

      if (cart && typeof cart.getSectionsToRender === 'function') {
        const sectionIds = cart.getSectionsToRender().map((section) => section.id);
        if (sectionIds.length) formData.append('sections', sectionIds.join(','));
      }

      formData.append('sections_url', window.location.pathname);

      if (cart && typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(activeElement || document.activeElement);
      }

      dispatch(root, 'pro:cart:add:start', { items: normalizedItems, activeElement, source });

      standardErrorCode = 'SERVICE_UNAVAILABLE';
      const response = await fetch(getCartAddUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      data = await response.json();
      if (!response.ok || data.status) {
        standardErrorCode = 'INVALID';
        throw data;
      }
    } catch (error) {
      const message =
        error && (error.description || error.message)
          ? error.description || error.message
          : 'Unable to add to cart. Please try again.';

      console.error('[PawlishProductCartCoordinator]', error);
      showError(productForm, message);
      await publishCartError(error, productVariantId, source);

      if (useStandardEvents) {
        dispatchCartErrorEvent(standardEventTarget, message, standardErrorCode);
        rejectCartLinesUpdateEvent(linesUpdateDeferred, new Error(message));
      }

      dispatch(root, 'pro:cart:add:error', {
        error,
        message,
        items: normalizedItems,
        activeElement,
        source,
      });
      throw error;
    }

    try {
      markGiftItemsAdded(root, normalizedItems);
    } catch (error) {
      console.warn('[PawlishProductCartCoordinator] Unable to update gift state after add.', error);
    }

    if (useStandardEvents) {
      resolveCartLinesUpdateEvent(linesUpdateDeferred);
    }

    try {
      renderCart(data, activeElement);
    } catch (error) {
      console.warn('[PawlishProductCartCoordinator] Cart was updated, but the cart UI could not render.', error);
      dispatch(root, 'pro:cart:ui:error', {
        error,
        response: data,
        items: normalizedItems,
        activeElement,
        source,
      });
    }

    dispatch(root, 'pro:cart:add:success', {
      response: data,
      items: normalizedItems,
      activeElement,
      source,
    });
    void publishCartUpdate(data, productVariantId, source);
    return data;
  }

  function addItems(form, root, activeElement) {
    if (!form || !root) return Promise.resolve(false);
    if (pendingRequests.has(root)) return pendingRequests.get(root);

    const productForm = getProductFormElement(form);
    const productVariantId = getMainVariantId(form);
    let items = [];

    showError(productForm, '');
    setEnhancedLoading(root, productForm, true);

    const request = (async () => {
      items = await buildItems(form, root);
      return submitPreparedItemsRequest(items, root, activeElement, {
        productForm,
        productVariantId,
        source: 'product-form',
        standardEventTarget: getStandardEventTarget(productForm, root),
        useStandardEvents: !isThemeEditor(),
      });
    })().finally(() => {
      setEnhancedLoading(root, productForm, false);
      refreshBundleControls(root);
      pendingRequests.delete(root);
      dispatch(root, 'pro:cart:add:end', { items, activeElement, source: 'product-form' });
    });

    pendingRequests.set(root, request);
    return request;
  }

  function addPreparedItems(items, root, activeElement, options = {}) {
    if (!root || !root.nodeType) return Promise.reject(new Error('A valid cart source element is required.'));
    if (pendingRequests.has(root)) return pendingRequests.get(root);

    const normalizedItems = groupItems(Array.isArray(items) ? items : []);
    const control = activeElement && activeElement.nodeType ? activeElement : null;
    const source = options.source || 'external';

    if (control) setSecondaryButtonLoading(control, true);

    const request = submitPreparedItemsRequest(normalizedItems, root, control, {
      productVariantId: String(normalizedItems[0] ? normalizedItems[0].id : ''),
      source,
      standardEventTarget: root,
      useStandardEvents: !isThemeEditor(),
    }).finally(() => {
      if (control) setSecondaryButtonLoading(control, false);
      pendingRequests.delete(root);
      dispatch(root, 'pro:cart:add:end', {
        items: normalizedItems,
        activeElement: control,
        source,
      });
    });

    pendingRequests.set(root, request);
    return request;
  }

  function submitNative(form, root, activeElement) {
    const button = getMainButton(form);
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return false;

    const externalTrigger =
      activeElement && activeElement !== button && activeElement.matches('[data-pro-sticky-atc-submit]')
        ? activeElement
        : null;

    let observer = null;

    if (externalTrigger) {
      setSecondaryButtonLoading(externalTrigger, true);
      dispatch(root, 'pro:cart:add:start', { activeElement: externalTrigger, native: true });

      observer = new MutationObserver(() => {
        if (button.classList.contains('loading')) return;

        observer.disconnect();
        setSecondaryButtonLoading(externalTrigger, false);
        dispatch(root, 'pro:cart:add:end', { activeElement: externalTrigger, native: true });
      });

      observer.observe(button, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(button);
    } else {
      button.click();
    }

    if (externalTrigger && !button.classList.contains('loading')) {
      if (observer) observer.disconnect();
      setSecondaryButtonLoading(externalTrigger, false);
      dispatch(root, 'pro:cart:add:end', { activeElement: externalTrigger, native: true });
    }

    return true;
  }

  function submitForRoot(rootOrElement, activeElement) {
    const root = getProductRoot(rootOrElement);
    const form = getProductForm(root);
    if (!root || !form) return false;

    if (shouldEnhance(form, root)) {
      const prepared = prepareThemeEditorNativeBundle(form, root);

      if (prepared) {
        const submitted = submitNative(form, root, activeElement);
        schedulePreparedCleanup(prepared);
        return submitted;
      }

      addItems(form, root, activeElement).catch(() => {});
      return true;
    }

    return submitNative(form, root, activeElement);
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || !form.matches || !form.matches('form[data-pro-cart-coordinator-form]')) return;

      const root = getProductRoot(form, form.dataset.proSectionId);
      if (!root || !shouldEnhance(form, root)) return;

      const prepared = prepareThemeEditorNativeBundle(form, root);
      if (prepared) {
        showError(getProductFormElement(form), '');
        schedulePreparedCleanup(prepared);
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const activeElement = event.submitter || getMainButton(form) || document.activeElement;
      addItems(form, root, activeElement).catch(() => {});
    },
    listenerOptions
  );

  document.addEventListener(
    'click',
    (event) => {
      const proxyButton =
        event.target.closest &&
        event.target.closest('[data-pro-bundle-proxy], .bqu-btn--proxy[id^="bqu-btn-"]');
      if (!proxyButton) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (proxyButton.disabled) return;

      const root = getProductRoot(proxyButton, proxyButton.dataset.sectionId);
      const form = getProductForm(root);
      if (!root || !form) return;

      const prepared = prepareThemeEditorNativeBundle(form, root);
      if (prepared) {
        submitNative(form, root, proxyButton);
        schedulePreparedCleanup(prepared);
        return;
      }

      addItems(form, root, proxyButton).catch(() => {});
    },
    listenerOptions
  );

  window.PawlishProductCartCoordinator = {
    version: VERSION,
    buildItems,
    getProductRoot,
    getSelectedBundleCard,
    getSelectedUpsellItems,
    getEligibleFreeGiftItems,
    getFreeGiftBlocks,
    groupItems,
    shouldEnhance,
    submitForRoot,
    addPreparedItems,
  };

  if (!window[LOADER_KEY]) {
    window[LOADER_KEY] = Promise.resolve(window.PawlishProductCartCoordinator);
  }
})();
